import { RxBarChart } from '@react-icons/all-files/rx/RxBarChart'
import { RxCopy } from '@react-icons/all-files/rx/RxCopy'
import { RxDownload } from '@react-icons/all-files/rx/RxDownload'
import { RxInput } from '@react-icons/all-files/rx/RxInput'
import { RxMixerHorizontal } from '@react-icons/all-files/rx/RxMixerHorizontal'
import { RxPlusCircled } from '@react-icons/all-files/rx/RxPlusCircled'
import { RxRows } from '@react-icons/all-files/rx/RxRows'
import { RxShare1 } from '@react-icons/all-files/rx/RxShare1'
import { RxTrash } from '@react-icons/all-files/rx/RxTrash'
import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import api from '@disco/api/server'
import { PHONE } from '@disco/constants/DeviceTypes'
import { useTranslation } from '@disco/i18n/use-translation'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  copyPlaylistToDiscoAndOpenInShortlists,
  copyToDisco,
} from '@disco/state-management/actions/ExplorerActions'
import {
  getPlaylistParams,
  loadPlaylistZipUrls,
} from '@disco/state-management/actions/playlist-actions'
import { removeLockedPlaylists } from '@disco/state-management/actions/ServerActions'
import { getTeamLibrary } from '@disco/state-management/selectors/groups'
import {
  getIsPlaylistInReceivedSharedChannel,
  getPlaylist,
  makeGetIfLockedPlaylistBelongsToRegularGroups,
  makeGetPlaylistRestrictions,
} from '@disco/state-management/selectors/playlists'
import {
  getConstanceSettings,
  getUser,
} from '@disco/state-management/selectors/user'
import captureError, { parseServerError } from '@disco/utils/captureError'
import { isChannelShared } from '@disco/utils/groups'
import { startPendingUINotification } from '@disco/utils/notifications'
import { downloadInFrameIfSupported } from '@disco/utils/target-iframe'
import { getZipDownloadsLinks } from '@disco/utils/tracks'

import type { Group } from 'types/group'
import type { PlaylistId } from 'types/playlist'

import { MixpanelEventLocationContext } from 'contexts/mixpanel-event-location'

import DropdownMenuSections from 'components/common/dropdown-menu-sections'
import { DropdownMenu, Flex } from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

import AddToShortlist from './add-to-shortlist'
import EditPlaylist from './edit-playlist'
import PlaylistSettings from './playlist-settings'

type Props = {
  group: Group | null | undefined
  playlist: { playlist_id: PlaylistId } | null | undefined
  addToCustomGroup: boolean
  addToShortlist: boolean
  deletePlaylist: boolean
  downloadZip: boolean
  editMetadata: boolean
  editPlaylist: boolean
  playlistStats: boolean
  sharePlaylist: boolean
  trackOrder: boolean
  onPlaylistSettingsAwait: () => void
}

const PlaylistDropdown = ({
  group,
  playlist: playlistProp,
  addToCustomGroup,
  addToShortlist,
  deletePlaylist,
  downloadZip,
  editMetadata,
  editPlaylist,
  playlistStats,
  sharePlaylist,
  trackOrder,
  onPlaylistSettingsAwait,
}: Props): React.ReactNode => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation('playlists')
  const { featureContext } = React.useContext(MixpanelEventLocationContext)

  const getPlaylistRestrictions = React.useMemo(makeGetPlaylistRestrictions, [])
  const getIfLockedPlaylistBelongsToRegularGroups = React.useMemo(
    makeGetIfLockedPlaylistBelongsToRegularGroups,
    [],
  )

  const playlist = useSelector(state =>
    playlistProp ? getPlaylist(state, { id: playlistProp.playlist_id }) : null,
  )
  const user = useSelector(getUser)
  const restrictions = useSelector(state =>
    playlist
      ? getPlaylistRestrictions(state, { id: playlist.playlist_id })
      : null,
  )
  const displayRemoveFromBrowse = useSelector(state =>
    playlist
      ? getIfLockedPlaylistBelongsToRegularGroups(state, {
          id: playlist.playlist_id,
        })
      : false,
  )
  const isPhone = useSelector(state => state.app.mobileDeviceType === PHONE)
  const metadataCsvExportEnabled = useSelector(
    state => !!getConstanceSettings(state).PLT_448_DOWNLOAD_METADATA_CSV_EXPORT,
  )
  const teamLibrary = useSelector(getTeamLibrary)
  const isInReceivedSharedChannel = useSelector(state =>
    playlist
      ? getIsPlaylistInReceivedSharedChannel(state, {
          id: playlist.playlist_id,
        })
      : false,
  )

  React.useEffect(() => {
    if (!playlist) return
    if (
      user.business.private_metadata.territories_enabled &&
      playlist.has_any_territoryless_track === undefined
    ) {
      dispatch(getPlaylistParams(playlist))
    }
    if (!playlist.zip_urls) {
      dispatch(loadPlaylistZipUrls(playlist))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlist?.playlist_id])

  if (!playlist) return null

  const { playlist_id } = playlist
  const groupId = group?.id
  const disableDelete = restrictions?.disableDelete ?? false

  const share = (() => {
    if (!sharePlaylist || !playlist.permissions?.share_link) return null
    const { has_any_territoryless_track } = playlist
    const territoriesEnabled =
      user.business.private_metadata.territories_enabled
    const link =
      territoriesEnabled &&
      (has_any_territoryless_track || user.territories.length > 1)
        ? `/explorer/playlists/${playlist_id}/share-warning`
        : `/explorer/playlists/${playlist_id}/share`
    const disabled =
      territoriesEnabled && has_any_territoryless_track === undefined
    return (
      <DropdownMenu.Item
        disabled={disabled}
        onSelect={() => {
          dispatch(sendMixpanelEvent('Share playlist via playlist menu'))
          if (featureContext === 'showcases') {
            dispatch(
              sendMixpanelEvent('Manage Artist modal - showcase - Share'),
            )
          }
          navigate(link)
        }}
      >
        <Flex align="center" gap="2">
          <RxShare1 />
          Share
        </Flex>
      </DropdownMenu.Item>
    )
  })()

  const stats = (() => {
    if (!playlistStats || !playlist.permissions?.manage) return null
    return (
      <DropdownMenu.Item
        onSelect={() => {
          if (featureContext === 'showcases') {
            dispatch(
              sendMixpanelEvent('Manage Artist modal - showcase - Check stats'),
            )
          }
          dispatch(sendMixpanelEvent('Access playlist stats via playlist menu'))
          navigate(`/explorer/playlists/${playlist_id}/stats`)
        }}
      >
        <Flex align="center" gap="2">
          <RxBarChart />
          Stats
        </Flex>
      </DropdownMenu.Item>
    )
  })()

  const rename = (() => {
    if (!editMetadata || !playlist.permissions?.manage) return null
    return (
      <DropdownMenu.Item
        onSelect={() => {
          dispatch(sendMixpanelEvent('Rename playlist via playlist menu'))
          if (featureContext === 'showcases') {
            dispatch(
              sendMixpanelEvent('Manage Artist modal - showcase - Rename'),
            )
          }
          navigate(`/explorer/playlists/${playlist_id}/metadata`)
        }}
      >
        <Flex align="center" gap="2">
          <RxInput />
          Rename
        </Flex>
      </DropdownMenu.Item>
    )
  })()

  const editAllMetadata = (() => {
    if (!playlist.permissions?.manage) return null
    return (
      <DropdownMenu.Item
        onSelect={() => {
          dispatch(sendMixpanelEvent('Open edit multiple via playlist menu'))
          if (featureContext === 'showcases') {
            dispatch(
              sendMixpanelEvent(
                'Manage Artist modal - showcase - Edit all track metadata',
              ),
            )
          }
          navigate(`/explorer/playlists/${playlist_id}/bulk-edit`)
        }}
      >
        <Flex align="center" gap="2">
          <RxMixerHorizontal />
          Edit all track metadata
        </Flex>
      </DropdownMenu.Item>
    )
  })()

  const editPlaylistItem = (() => {
    if (!editPlaylist || !playlist.permissions?.manage) return null
    return (
      <EditPlaylist
        key="edit-playlist"
        group={group}
        playlist={playlist}
        behavior="edit"
      />
    )
  })()

  const duplicatePlaylistItem = (() => {
    if (!editPlaylist || !playlist.permissions?.manage) return null
    return (
      <EditPlaylist
        key="duplicate-playlist"
        group={group}
        playlist={playlist}
        behavior="duplicate"
      />
    )
  })()

  const trackOrderItem = (() => {
    if (!trackOrder || !playlist.permissions?.manage) return null
    return (
      <DropdownMenu.Item
        onSelect={() => {
          dispatch(sendMixpanelEvent('Change track order via playlist menu'))
          if (featureContext === 'showcases') {
            dispatch(
              sendMixpanelEvent(
                'Manage Artist modal - showcase - Change track order',
              ),
            )
          }
          navigate(`/explorer/playlists/${playlist_id}/track-order`)
        }}
      >
        <Flex align="center" gap="2">
          <RxRows />
          Change track order
        </Flex>
      </DropdownMenu.Item>
    )
  })()

  const playlistSettingsItem = (() => {
    if (!playlist.permissions?.manage) return null
    return (
      <PlaylistSettings
        playlist={playlist}
        onNeedsAwait={onPlaylistSettingsAwait}
      />
    )
  })()

  const addToChannelItem = (() => {
    if (!addToCustomGroup || !playlist.permissions?.manage) return null
    return (
      <DropdownMenu.Item
        onSelect={() => {
          dispatch(sendMixpanelEvent('Add to channel via playlist menu'))
          if (featureContext === 'showcases') {
            dispatch(
              sendMixpanelEvent(
                'Manage Artist modal - showcase - Add to channel',
              ),
            )
          }
          navigate(
            `/explorer/playlists/${playlist_id}/add-to-channel${
              groupId ? `?source_group=${groupId}` : ''
            }`,
          )
        }}
      >
        <Flex align="center" gap="2">
          <RxPlusCircled />
          Add to channel
        </Flex>
      </DropdownMenu.Item>
    )
  })()

  const addToShortlistItem = (() => {
    if (!isPhone || !addToShortlist || !playlist.permissions?.manage)
      return null
    return (
      <AddToShortlist
        playlist={playlist}
        sourceGroupId={group?.id ?? null}
        sourceGroupType={group?.group_type ?? null}
      />
    )
  })()

  const copyToItem = (() => {
    if (playlist.permissions?.manage || !groupId) return null
    return (
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger>
          <Flex align="center" gap="2">
            <RxCopy />
            Copy to
          </Flex>
        </DropdownMenu.SubTrigger>
        <DropdownMenu.SubContent>
          <DropdownMenu.Item
            onSelect={() => {
              const teamLibraryId = teamLibrary?.id
              const objectUrl = playlist.view_playlist_url
              if (!teamLibraryId) return
              dispatch(
                copyToDisco(
                  playlist.name,
                  objectUrl,
                  { groupIds: [teamLibraryId] },
                  groupId,
                ),
              )
              mixpanel.sendPureEvent('Copy Shared Item', {
                Context: 'Shared Channels',
                Playlist_Name: playlist.name,
                Location: 'Shared Channel - Track Action Menu',
                Copy_To_Location: 'Browse',
                Copy: 'Browse',
                Type: 'Playlist',
                'Channel ID': groupId,
                'Channel Owner': group?.business?.id,
              })
            }}
          >
            Browse
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() => {
              navigate(
                `/explorer/playlists/${playlist_id}/${groupId}/copy-to-channel`,
              )
              mixpanel.sendPureEvent('Copy Shared Item', {
                Context: 'Shared Channels',
                Playlist_Name: playlist.name,
                Location: 'Shared Channel - Track Action Menu',
                Copy_To_Location: 'Channel',
                Copy: 'Channel',
                Type: 'Playlist',
                'Channel ID': groupId,
                'Channel Owner': group?.business?.id,
              })
            }}
          >
            Channel
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() => {
              mixpanel.sendPureEvent('Copy Shared Item', {
                Context: 'Shared Channels',
                Playlist_Name: playlist.name,
                Location: 'Shared Channel - Track Action Menu',
                Copy_To_Location: 'Playlist Creator',
                Copy: 'Playlist Creator',
                Type: 'Playlist',
                'Channel ID': groupId,
                'Channel Owner': group?.business?.id,
              })
              dispatch(copyPlaylistToDiscoAndOpenInShortlists(playlist))
            }}
          >
            Playlist Creator
          </DropdownMenu.Item>
        </DropdownMenu.SubContent>
      </DropdownMenu.Sub>
    )
  })()

  const handleExportMetadataCsv = async () => {
    dispatch(sendMixpanelEvent('Download metadata csv via playlist menu'))
    if (featureContext === 'showcases') {
      dispatch(
        sendMixpanelEvent(
          'Manage Artist modal - showcase - Download metadata csv',
        ),
      )
    }
    const updateNotification = startPendingUINotification(
      t(
        'playlists:generatingMetadataCsvForPlaylist',
        "Generating metadata CSV for '{{playlistName}}'…",
        { playlistName: playlist.name },
      ),
    )
    try {
      const { download_url } = await api.exportTrackMetadata({
        playlist_version_id: playlist.id,
        export_nested_tracks: true,
        export_native_tags: true,
        include_writers: true,
      })
      if (download_url) {
        const a = document.createElement('a')
        a.href = download_url
        a.download = ''
        const target = downloadInFrameIfSupported()
        if (target) a.target = target
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        updateNotification({
          message: t('playlists:metadataCsvReady', 'Metadata CSV ready.'),
          level: 'success',
          dismissible: 'click',
        })
      } else {
        updateNotification({
          message: t(
            'playlists:exportTakingAWhileWillEmail',
            "This export is taking a while — we'll email it to you when it's ready.",
          ),
          level: 'success',
          dismissible: 'click',
        })
      }
    } catch (error) {
      updateNotification({
        message: parseServerError(error),
        level: 'error',
        dismissible: 'click',
      })
      captureError(error)
    }
  }

  const downloadZipItem = (() => {
    if (!downloadZip) return null
    const links = getZipDownloadsLinks(playlist.zip_urls, 'inapp').map(
      ({ title, url }, index) => (
        <DropdownMenu.Item
          key={`download-${index}`}
          onSelect={() => {
            dispatch(
              sendMixpanelEvent(
                `Download ${title.toLowerCase()} via playlist menu`,
                {
                  ...(isChannelShared(group ?? undefined)
                    ? { Context: 'Shared Channels' }
                    : {}),
                },
              ),
            )
            if (featureContext === 'showcases') {
              dispatch(
                sendMixpanelEvent(
                  'Manage Artist modal - showcase - Download playlist',
                ),
              )
            }
            const a = document.createElement('a')
            a.href = url
            a.download = ''
            const target = downloadInFrameIfSupported()
            if (target) a.target = target
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
          }}
        >
          {title}
        </DropdownMenu.Item>
      ),
    )
    if (metadataCsvExportEnabled && !isInReceivedSharedChannel) {
      links.push(
        <DropdownMenu.Item
          key="download-metadata-csv"
          onSelect={handleExportMetadataCsv}
        >
          {t('playlists:metadataCsv', 'Metadata (CSV)')}
        </DropdownMenu.Item>,
      )
    }
    if (links.length === 0) return null
    return (
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger>
          <Flex align="center" gap="2">
            <RxDownload />
            {t('playlists:downloadPlaylist', 'Download playlist')}
          </Flex>
        </DropdownMenu.SubTrigger>
        <DropdownMenu.SubContent>{links}</DropdownMenu.SubContent>
      </DropdownMenu.Sub>
    )
  })()

  const removeFromBrowseItem = (() => {
    if (!displayRemoveFromBrowse) return null
    return (
      <DropdownMenu.Item
        color="red"
        onSelect={() => {
          dispatch(removeLockedPlaylists([playlist]))
        }}
      >
        Remove from Browse
      </DropdownMenu.Item>
    )
  })()

  const deletePlaylistItem = (() => {
    if (!deletePlaylist || !playlist.permissions?.delete) return null
    return (
      <DropdownMenu.Item
        color="red"
        disabled={disableDelete}
        onSelect={() => {
          dispatch(sendMixpanelEvent('Delete playlist via playlist menu'))
          if (featureContext === 'showcases') {
            dispatch(
              sendMixpanelEvent(
                'Manage Artist modal - showcase - Delete playlist',
              ),
            )
          }
          navigate(`/explorer/playlists/${playlist_id}/delete`)
        }}
      >
        <Flex align="center" gap="2">
          <RxTrash />
          Delete playlist
        </Flex>
      </DropdownMenu.Item>
    )
  })()

  return (
    <DropdownMenuSections
      sections={[
        { id: 'primary', items: [share, stats] },
        {
          id: 'edit',
          label: 'Edit',
          items: [
            rename,
            editAllMetadata,
            editPlaylistItem,
            duplicatePlaylistItem,
            trackOrderItem,
          ],
        },
        {
          id: 'actions',
          label: 'Actions',
          items: [
            addToChannelItem,
            addToShortlistItem,
            copyToItem,
            downloadZipItem,
            playlistSettingsItem,
          ],
        },
        {
          id: 'danger',
          items: [removeFromBrowseItem, deletePlaylistItem],
        },
      ]}
    />
  )
}

export default PlaylistDropdown
